import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import type { MailContactBlock } from "./mailContactBlock";
import {
  createEmptyMailContactBlock,
  ensureMailContactItems,
  parseMailContactTable,
  serializeMailContactBlock,
} from "./mailContactBlock";
import type { MailGoogleReviewBlock } from "./mailGoogleReviewBlock";
import {
  createEmptyMailGoogleReviewBlock,
  parseMailGoogleReviewTable,
  serializeMailGoogleReviewBlock,
} from "./mailGoogleReviewBlock";
import type { MailUnsubscribeBlock } from "./mailUnsubscribeBlock";
import {
  createEmptyMailUnsubscribeBlock,
  parseMailUnsubscribeTable,
  serializeMailUnsubscribeBlock,
} from "./mailUnsubscribeBlock";
import type { MailLinePart, MailParagraphContent } from "./mailLineParts";
import {
  appendPlaceholderToParts,
  mailPhToken,
  mergeAdjacentTextParts,
  paragraphRichDefault,
  parseHtmlToMailLineParts,
  parsePlainStringToMailLineParts,
  serializeConditionalParagraphToInnerTemplate,
  serializeMailLinePartsToInnerHtml,
  trimmedHtmlStartsWithGoIfDirective,
  tryParseConditionalTemplateHtml,
} from "./mailLineParts";

export type MailBodyBlock =
  | { id: string; type: "title"; parts: MailLinePart[] }
  | {
      id: string;
      type: "paragraph";
      variant: "body" | "tight";
      content: MailParagraphContent;
    }
  | {
      id: string;
      type: "paragraph";
      variant: "boxed";
      parts: MailLinePart[];
    }
  | { id: string; type: "footer"; variant: "note" | "signature"; parts: MailLinePart[] }
  | { id: string; type: "html"; html: string }
  | MailContactBlock
  | MailGoogleReviewBlock
  | MailUnsubscribeBlock;

export type {
  MailLinePart,
  MailParagraphContent,
  MailPhStyle,
  TextEmphasis,
} from "./mailLineParts";
export { mailPhToken } from "./mailLineParts";

export const MAIL_BLOCK_LABELS: Record<MailBodyBlock["type"], string> = {
  title: "Titre",
  paragraph: "Paragraphe",
  footer: "Footer",
  contact: "Contact",
  googleReview: "Google Avis",
  unsubscribe: "Se désinscrire",
  html: "HTML",
};

export const MAIL_BLOCK_TYPES: MailBodyBlock["type"][] = [
  "title",
  "paragraph",
  "footer",
  "contact",
  "googleReview",
  "unsubscribe",
  "html",
];

export const MAIL_BODY_RICH_FIELD_KEY = "rich" as const;

export const MAIL_BODY_CONDITIONAL_IF_FIELD_KEY = "condIf" as const;

export const MAIL_BODY_CONDITIONAL_ELSE_FIELD_KEY = "condElse" as const;

export const MAIL_BODY_CONDITIONAL_CONDITION_FIELD_KEY = "condCondition" as const;

const FONT = "Verdana, Geneva, sans-serif";

const pTitle = (inner: string) =>
  `<p style="font-size: 18px; margin: 0 0 16px 0; color: #111827; font-family: ${FONT};">${inner}</p>`;

const pBody = (inner: string) =>
  `<p style="font-size: 14px; line-height: 1.6; color: #374151; margin: 16px 0; font-family: ${FONT}; word-wrap: break-word;">${inner}</p>`;

const pBodyTight = (inner: string) =>
  `<p style="font-size: 14px; line-height: 1.6; color: #374151; margin: 0 0 16px 0; font-family: ${FONT}; word-wrap: break-word;">${inner}</p>`;

const grayBoxWrap = (inner: string) => `
<table role="presentation" width="100%" style="border-collapse: separate; border-spacing: 0; width: 100%; margin: 16px 0;">
  <tr>
    <td style="padding: 0; width: 100%; box-sizing: border-box;">
      <div data-mail-box="1" style="padding: 16px; border: 1px solid #e5e7eb; border-radius: 5px; background-color: #f9fafb; overflow: hidden; display: block; width: 100%; box-sizing: border-box; max-width: 100%;">
        ${inner}
      </div>
    </td>
  </tr>
</table>`;

const noteSmallWrap = (inner: string) => `
<table role="presentation" width="100%" style="border-collapse: separate; border-spacing: 0; width: 100%; margin: 16px 0;">
  <tr>
    <td style="padding: 0; width: 100%; box-sizing: border-box;">
      <div data-mail-box="1" style="padding: 10px; border: 1px solid #DDDDDD; background-color: #F9F9F9; overflow: hidden; display: block; width: 100%;">
        <p style="font-family: ${FONT}; font-size: 14px; margin: 0;">
          <span style="font-size: 12px;"><i><span style="color: #616161;">${inner}</span></i></span>
        </p>
      </div>
    </td>
  </tr>
</table>`;

function serializeParagraphContent(
  content: MailParagraphContent,
  tight: boolean,
  catalog?: readonly MailPlaceholderDefinition[],
): string {
  if (content.format === "conditional") {
    const inner = serializeConditionalParagraphToInnerTemplate(
      content.conditionField,
      content.ifParts,
      content.elseParts,
      catalog,
    );

    return tight ? pBodyTight(inner) : pBody(inner);
  }

  if (content.format === "template") {
    return tight ? pBodyTight(content.html.trim()) : pBody(content.html.trim());
  }

  const inner = serializeMailLinePartsToInnerHtml(content.parts, catalog);

  return tight ? pBodyTight(inner) : pBody(inner);
}

export function newBlockId(): string {
  return `bk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyMailBodyBlock(type: MailBodyBlock["type"]): MailBodyBlock | null {
  const id = newBlockId();

  switch (type) {
    case "title":
      return {
        id,
        type: "title",
        parts: mergeAdjacentTextParts([
          { kind: "text", text: "Bonjour " },
          { kind: "ph", key: "Name", style: "none" },
          { kind: "text", text: "," },
        ]),
      };
    case "paragraph":
      return { id, type: "paragraph", variant: "body", content: paragraphRichDefault() };
    case "footer":
      return {
        id,
        type: "footer",
        variant: "note",
        parts: mergeAdjacentTextParts([{ kind: "text", text: "Note pour le destinataire." }]),
      };
    case "html":
      return { id, type: "html", html: "" };
    case "contact":
      return createEmptyMailContactBlock(id);
    case "googleReview":
      return createEmptyMailGoogleReviewBlock(id);
    case "unsubscribe":
      return createEmptyMailUnsubscribeBlock(id);
    default:
      return null;
  }
}

export function serializeBlocks(
  blocks: MailBodyBlock[],
  catalog?: readonly MailPlaceholderDefinition[],
): string {
  return blocks.map((block) => serializeBlock(block, catalog)).join("\n\n");
}

function serializeBlock(
  block: MailBodyBlock,
  catalog?: readonly MailPlaceholderDefinition[],
): string {
  switch (block.type) {
    case "title":
      return pTitle(serializeMailLinePartsToInnerHtml(block.parts, catalog));
    case "paragraph":
      if (block.variant === "boxed") {
        const inner = serializeMailLinePartsToInnerHtml(block.parts, catalog).trim();
        const pInner = inner
          ? `<p style="font-size: 14px; line-height: 1.6; color: #374151; margin: 0 0 8px 0; word-wrap: break-word;">${inner}</p>`
          : `<p style="font-size: 14px; line-height: 1.6; color: #374151; margin: 0 0 8px 0; word-wrap: break-word;"><br></p>`;

        return grayBoxWrap(pInner);
      }

      return serializeParagraphContent(block.content, block.variant === "tight", catalog);
    case "footer":
      if (block.variant === "note") {
        return noteSmallWrap(serializeMailLinePartsToInnerHtml(block.parts, catalog));
      }

      return `<p style="font-size: 16px; font-weight: 600; color: #111827; margin: 16px 0; font-family: ${FONT};">${serializeMailLinePartsToInnerHtml(
        block.parts,
        catalog,
      )}</p>`;
    case "html":
      return block.html;
    case "contact":
      return serializeMailContactBlock(block);
    case "googleReview":
      return serializeMailGoogleReviewBlock(block);
    case "unsubscribe":
      return serializeMailUnsubscribeBlock();
    default:
      return "";
  }
}

export function emptyBlocksForNewTemplate(): MailBodyBlock[] {
  return [
    { id: newBlockId(), type: "paragraph", variant: "body", content: paragraphRichDefault() },
  ];
}

export function parseHtmlToBlocks(html: string): MailBodyBlock[] | null {
  const trimmed = html.trim();

  if (!trimmed) {
    return [];
  }

  try {
    const doc = new DOMParser().parseFromString(
      `<div id="zekay-parse-root">${trimmed}</div>`,
      "text/html",
    );
    const root = doc.getElementById("zekay-parse-root");

    if (!root) {
      return null;
    }

    const blocks: MailBodyBlock[] = [];

    for (const node of Array.from(root.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = (node.textContent ?? "").trim();

        if (!t) {
          continue;
        }

        blocks.push({
          id: newBlockId(),
          type: "paragraph",
          variant: "body",
          content: paragraphContentFromInner(t),
        });

        continue;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const parsed = parseTopLevelNode(node as HTMLElement);

        if (parsed) {
          blocks.push(...parsed);
        }
      }
    }

    if (blocks.length === 0) {
      return null;
    }

    return blocks;
  } catch {
    return null;
  }
}

function parseTopLevelNode(el: HTMLElement): MailBodyBlock[] | null {
  const tag = el.tagName.toLowerCase();

  if (tag === "span") {
    const nested = Array.from(el.children).flatMap(
      (c) => parseTopLevelNode(c as HTMLElement) ?? [],
    );

    if (nested.length > 0) {
      return nested;
    }

    const t = el.innerHTML.trim();

    return t
      ? [
          {
            id: newBlockId(),
            type: "paragraph",
            variant: "body",
            content: { format: "rich", parts: parseHtmlToMailLineParts(t) },
          },
        ]
      : null;
  }

  if (tag === "p") {
    return [parseParagraphLike(el)];
  }

  if (tag === "table") {
    const contact = parseMailContactTable(el, newBlockId());

    if (contact) {
      return [contact];
    }

    const googleReview = parseMailGoogleReviewTable(el, newBlockId());

    if (googleReview) {
      return [googleReview];
    }

    const unsubscribe = parseMailUnsubscribeTable(el, newBlockId());

    if (unsubscribe) {
      return [unsubscribe];
    }

    const inner = el.innerHTML;

    if (inner.includes("#FFD000") || inner.includes("FFD000")) {
      return [{ id: newBlockId(), type: "html", html: el.outerHTML }];
    }

    if (inner.includes("#f9fafb") || inner.includes("f9fafb") || inner.includes("#F9F9F9")) {
      return [parseGrayBoxTable(el)];
    }

    return [{ id: newBlockId(), type: "html", html: el.outerHTML }];
  }

  if (tag === "div") {
    return [{ id: newBlockId(), type: "html", html: el.outerHTML }];
  }

  return [{ id: newBlockId(), type: "html", html: el.outerHTML }];
}

function paragraphContentFromInner(inner: string): MailParagraphContent {
  const trimmed = inner.trim();

  if (trimmedHtmlStartsWithGoIfDirective(trimmed)) {
    const parsed = tryParseConditionalTemplateHtml(trimmed);

    if (parsed) {
      return { format: "conditional", ...parsed };
    }

    return {
      format: "rich",
      parts: mergeAdjacentTextParts(parseHtmlToMailLineParts(trimmed)),
    };
  }

  return { format: "rich", parts: mergeAdjacentTextParts(parseHtmlToMailLineParts(trimmed)) };
}

function parseParagraphLike(el: HTMLElement): MailBodyBlock {
  const style = el.getAttribute("style") || "";
  const inner = el.innerHTML.trim();
  const id = newBlockId();

  if (style.includes("18px")) {
    return { id, type: "title", parts: parseHtmlToMailLineParts(inner) };
  }

  if (
    style.includes("16px") &&
    (style.includes("font-weight: 600") || style.includes("font-weight:600"))
  ) {
    return { id, type: "footer", variant: "signature", parts: parseHtmlToMailLineParts(inner) };
  }

  if (style.includes("margin: 0 0 16px") || style.includes("margin:0 0 16px")) {
    return { id, type: "paragraph", variant: "tight", content: paragraphContentFromInner(inner) };
  }

  return { id, type: "paragraph", variant: "body", content: paragraphContentFromInner(inner) };
}

function parseGrayBoxTable(el: HTMLElement): MailBodyBlock {
  const div = el.querySelector("div[data-mail-box], div");
  const ps = div?.querySelectorAll("p") ?? [];
  const lines = Array.from(ps).map((p) => p.innerHTML.trim());

  if (lines.length === 0) {
    const inner = div?.innerHTML.trim() || "";

    return {
      id: newBlockId(),
      type: "footer",
      variant: "note",
      parts: parsePlainStringToMailLineParts(inner.replace(/<[^>]+>/g, " ").trim() || inner),
    };
  }

  const firstText = lines.join(" ");

  if (
    firstText.includes("automatique") ||
    firstText.includes("ne pas répondre") ||
    firstText.includes("Merci de ne pas")
  ) {
    return {
      id: newBlockId(),
      type: "footer",
      variant: "note",
      parts: parsePlainStringToMailLineParts(lines.map(stripTags).join(" ")),
    };
  }

  const parts = mergeBoxedParagraphPartsFromParagraphEls(ps);

  return { id: newBlockId(), type: "paragraph", variant: "boxed", parts };
}

function mergeBoxedParagraphPartsFromParagraphEls(ps: Iterable<Element>): MailLinePart[] {
  const arr = Array.from(ps) as HTMLParagraphElement[];
  const chunks: MailLinePart[] = [];

  arr.forEach((p, index) => {
    if (index > 0) {
      chunks.push({ kind: "text", text: "\n" });
    }

    const inner = p.innerHTML.trim();

    if (inner) {
      chunks.push(...parseHtmlToMailLineParts(inner));
    }
  });

  return mergeAdjacentTextParts(chunks.length > 0 ? chunks : [{ kind: "text", text: "" }]);
}

function stripTags(s: string): string {
  return s.replace(/<[^>]*>/g, "").trim();
}

export function extractMailBodyFromStoredHtml(rawHtml: string): string {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");
    Array.from(doc.querySelectorAll("style")).forEach((el) => el.remove());
    Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).forEach((el) => el.remove());
    const contentHost = doc.body?.querySelector('[data-mail-content="1"]');

    return contentHost ? (contentHost as HTMLElement).innerHTML : doc.body?.innerHTML || rawHtml;
  } catch {
    return rawHtml;
  }
}

export function hydrateBlocksFromStoredHtml(rawHtml: string): {
  blocks: MailBodyBlock[];
  html: string;
} {
  const body = extractMailBodyFromStoredHtml(rawHtml);
  let nextBlocks: MailBodyBlock[];

  if (!body.trim()) {
    nextBlocks = emptyBlocksForNewTemplate();
  } else {
    const parsed = parseHtmlToBlocks(body);
    nextBlocks =
      parsed && parsed.length > 0 ? parsed : [{ id: newBlockId(), type: "html", html: body }];
  }

  return { blocks: nextBlocks, html: serializeBlocks(nextBlocks) };
}

export function appendPhToBlockParts(block: MailBodyBlock, phKey: string): MailBodyBlock | null {
  switch (block.type) {
    case "title":
      return { ...block, parts: appendPlaceholderToParts(block.parts, phKey) };
    case "footer":
      return { ...block, parts: appendPlaceholderToParts(block.parts, phKey) };
    case "paragraph":
      if (block.variant === "boxed") {
        return { ...block, parts: appendPlaceholderToParts(block.parts, phKey) };
      }

      if (block.content.format === "conditional") {
        return {
          ...block,
          content: {
            ...block.content,
            ifParts: appendPlaceholderToParts(block.content.ifParts, phKey),
          },
        };
      }

      if (block.content.format === "template") {
        const parts = mergeAdjacentTextParts(parseHtmlToMailLineParts(block.content.html));

        return {
          ...block,
          content: {
            format: "rich",
            parts: appendPlaceholderToParts(parts, phKey),
          },
        };
      }

      return {
        ...block,
        content: { format: "rich", parts: appendPlaceholderToParts(block.content.parts, phKey) },
      };
    case "contact": {
      const items = ensureMailContactItems(block.items);
      const target = items.find((item) => item.enabled) ?? items[0];

      if (!target) {
        return block;
      }

      return {
        ...block,
        items: items.map((item) =>
          item.kind === target.kind ? { ...item, enabled: true, value: mailPhToken(phKey) } : item,
        ),
      };
    }
    case "googleReview":
      return { ...block, href: mailPhToken(phKey) };
    default:
      return null;
  }
}
